import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EmployeeDashboardUpdateDto } from "../../../types/dtos/dashboards";
import { FormattedDate } from "../../common";
import AppRoutes from "../../../routes/AppRoutes";

interface EmployeeUpdateBodyProps {
  /** The update to display information for */
  update: EmployeeDashboardUpdateDto;
  /** A function which changes the user's current journey and refreshes the journey widget */
  changeJourney: (journeyRef: string) => void;
}

function EmployeeUpdateBody({
  update,
  changeJourney,
}: EmployeeUpdateBodyProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  let buttonText = "Go";
  switch (update.updateType) {
    case "COLLABDOC":
      if (
        update.journey !== null &&
        update.journey.answerSetApprovalStatus !== null &&
        !update.journey.showAsWaitingForManager &&
        update.journey.answerSetApprovalStatus !==
          "INITIAL-SAVE-FORM-INCOMPLETE"
      ) {
        buttonText = t(
          "DashboardUpdate.Employee.Button.ReviewUpdates",
          "Review the updates",
        );
      } else {
        buttonText = t("DashboardUpdate.Employee.Button.Open", "Open");
      }
      break;
    case "DUALPREPMEETING":
      if (
        update.journey !== null &&
        update.journey.answerSetApprovalStatus !== null &&
        update.journey.answerSetApprovalStatus !==
          "INITIAL-SAVE-FORM-INCOMPLETE"
      ) {
        buttonText = t(
          "DashboardUpdate.Employee.Button.ReviewUpdates",
          "Review the updates",
        );
      } else {
        buttonText = t(
          "DashboardUpdate.Employee.Button.OpenMeeting",
          "Open the meeting",
        );
      }
      break;
    case "DUALPREPPLANNING":
      buttonText = t(
        "DashboardUpdate.Employee.Button.StartYourPlanning",
        "Start your planning",
      );
      break;
    case "NOTASKS":
    case "NOTENOUGHTASKS":
    case "OVERDUETASKS":
      if (update.task != null) {
        buttonText = t(
          "DashboardUpdate.Employee.Button.GoToTasks",
          "Go to #TASKS#",
        ).replace("#TASKS#", t(update.task.pluralNameTranslationKeyIdentifier));
      }
      break;
  }

  const onButtonClick = () => {
    if (
      update.updateType === "DUALPREPPLANNING" &&
      update.journey !== null &&
      update.journey.answerSetUniqueId === null
    ) {
      changeJourney(update.journey.journeyReference);
    } else if (update.journey !== null && update.journey.answerSetUniqueId) {
      const redirectUrl = AppRoutes.collaborativeDocument.generateRoute(
        update.journey.answerSetUniqueId,
        false,
        null,
      );
      navigate(redirectUrl);
    } else if (update.task != null) {
      const redirectUrl = AppRoutes.yourJourney.tasks.generatePathRoute(
        update.task.taskTypeId,
      );
      navigate(redirectUrl);
    }
  };

  return (
    <div className="pt-2">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        {update.journey && (
          <div className="hidden text-xs text-white/75 md:flex md:min-w-0 md:flex-grow">
            <span className="mr-[2px]">
              {t("Common.UpdatedBySentence.LastUpdated", "Last updated")}:
            </span>
            <FormattedDate
              date={update.journey.timestamp}
              displayMode="FROM-NOW"
            />
          </div>
        )}
        <div className="self-end text-right md:ml-auto">
          <button
            type="button"
            onClick={onButtonClick}
            className="inline-flex items-center justify-center gap-1 rounded-md bg-white px-4 py-1 font-medium text-gray-700 whitespace-nowrap transition-shadow motion-reduce:transition-none hover:shadow-md hover:shadow-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent active:scale-[0.98] motion-reduce:active:scale-100"
          >
            {buttonText}
            <FontAwesomeIcon icon={faArrowRight} size="xs" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmployeeUpdateBody;
